import useGuardedRealtimeList, { readListRows } from '../../realtime/useGuardedRealtimeList';
import { fetchBackgroundData } from '../../redux/api/portalApi';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { readNpSnapshot } from '../../redux/api/npSnapshot';
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { getProjectId, getProjectStatus } from "../../utils/dataNormalization";
import { formatDateUnified } from "../../utils/dateUtils";
import { calculateWeeksToShip, getWeeksToShipColor } from "../../utils/dateCalculations";
import useWeekCalculations from "../../functions/useWeekCalculations";

const NP_WEEK_COUNT = 52;

const HDPE_SUBTYPE_OPTIONS = [
  { value: "all", label: "All HDPE Types" },
  { value: "ez-fertigation", label: "EZ Fertigation" },
  { value: "bmx", label: "BMX" },
  { value: "horizontal-inducer", label: "Horizontal Inducer Tube" },
  { value: "rocket", label: "Vertical Inducer Tube (Rocket)" },
  { value: "suction-line", label: "Suction Line" },
];

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "enclosure", label: "Enclosure" },
  { value: "hdpe", label: "HDPE" },
];

function NPWeeklyScheduleTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiUrls = useSelector(selectApiUrls);
  const { weekColumns, getWeekForDate, refreshWeekData } = useWeekCalculations(NP_WEEK_COUNT);
  const initializedWeeksRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [hdpeSubtypeFilter, setHdpeSubtypeFilter] = useState("all");

  const fetchNpScheduleRows = useCallback(async ({ snapshotOnly = false, signal } = {}) => {
    const {
      generalProject,
      stationEnclosure,
      fertigation,
      pumpManifoldPiping,
      hcRequirements,
    } = apiUrls;

    if (!generalProject || !stationEnclosure || !fertigation || !pumpManifoldPiping || !hcRequirements) {
      return;
    }

    if (!snapshotOnly) { setLoading(true); setError(null); }
    const read = async url => snapshotOnly ? readListRows(await dispatch(fetchBackgroundData({ url, signal }))) : dispatch(readNpSnapshot(url)).unwrap();

    try {
      // Keep the same data sources used in NPJobs.
      const [
        generalProjectData,
        stationEnclosureData,
        fertigationData,
        pumpManifoldPipingData,
        hcRequirementsData,
      ] = await Promise.all([
        read(generalProject),
        read(stationEnclosure),
        read(fertigation),
        read(pumpManifoldPiping),
        read(hcRequirements),
      ]);

      const safeGeneralProject = Array.isArray(generalProjectData) ? generalProjectData : [];
      const safeStationEnclosure = Array.isArray(stationEnclosureData) ? stationEnclosureData : [];
      const safeFertigation = Array.isArray(fertigationData) ? fertigationData : [];
      const safePumpManifoldPiping = Array.isArray(pumpManifoldPipingData) ? pumpManifoldPipingData : [];
      const safeHcRequirements = Array.isArray(hcRequirementsData) ? hcRequirementsData : [];
      const projectsById = new Map(safeGeneralProject.map(project => [String(getProjectId(project)), project]));

      const resolveProjectForItem = (item, sourceTable) => {
        if (sourceTable === "generalProject") {
          return item;
        }

        return projectsById.get(String(getProjectId(item)));
      };

      const shouldIncludeByStatus = (statusValue) => {
        const normalizedStatus = (statusValue || "").toLowerCase();

        if (normalizedStatus === "shipped" || normalizedStatus === "canceled") {
          return false;
        }

        return normalizedStatus === "full release";
      };

      const resolveDueDateForRow = (project, item, preferredFields = []) => {
        const dueFieldPriority = [
          ...preferredFields,
          "NPCSMDue",
          "NPSuctionLineDue",
          "NPInducerTubeDue",
          "NPBMXDue",
          "NPFertigationDue",
        ];

        const uniqueFields = [...new Set(dueFieldPriority)];

        for (const field of uniqueFields) {
          const rawValue = project?.[field] ?? item?.[field];
          const normalizedDate = formatDateUnified(rawValue, "input");

          if (normalizedDate && /^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) {
            return {
              normalizedDueDate: normalizedDate,
              dueDateSource: field,
            };
          }
        }

        return {
          normalizedDueDate: null,
          dueDateSource: null,
        };
      };

      const createRow = (item, sourceTable, rowConfig) => {
        const project = resolveProjectForItem(item, sourceTable);
        const jobStatus = getProjectStatus(project || item);

        if (!shouldIncludeByStatus(jobStatus)) {
          return null;
        }

        const projectId = getProjectId(project) ?? getProjectId(item) ?? item.ID;
        const { normalizedDueDate, dueDateSource } = resolveDueDateForRow(
          project,
          item,
          rowConfig.dueFieldPriority || []
        );
        const dueSortValue = normalizedDueDate
          ? new Date(`${normalizedDueDate}T00:00:00`).getTime()
          : Number.POSITIVE_INFINITY;

        return {
          rowId: `${rowConfig.categoryKey}-${item.ID}-${projectId || "na"}`,
          type: rowConfig.typeLabel,
          typeKey: rowConfig.typeKey,
          hdpeSubtypeKey: rowConfig.hdpeSubtypeKey || null,
          hdpeSubtypeLabel: rowConfig.hdpeSubtypeLabel || "",
          jobNumber: project?.JobNumber || item.JobNumber || "N/A",
          ScheduleOnly: project?.ScheduleOnly ?? item.ScheduleOnly,
          projectName: project?.ProjectName || item.ProjectName || "N/A",
          modelNumber: project?.ModelNumber || item.ModelNumber || "N/A",
          jobStatus,
          normalizedDueDate,
          dueDateSource,
          dueSortValue,
          projectId,
          weeksToDue: calculateWeeksToShip(normalizedDueDate),
        };
      };

      const enclosureRows = safeStationEnclosure
        .filter((item) => item.EnclosureType === "CSM" && item.HasEnclosure === true)
        .map((item) =>
          createRow(item, "stationEnclosure", {
            categoryKey: "csm-enclosure",
            typeLabel: "Enclosure",
            typeKey: "enclosure",
            dueFieldPriority: ["NPCSMDue"],
          })
        )
        .filter(Boolean)
        .filter((row) => row.jobNumber !== "N/A");

      const ezFertigationRows = safeFertigation
        .filter(
          (item) =>
            item.HasFertigation === true &&
            item.WatertronicsFertigation &&
            item.WatertronicsFertigation.startsWith("EZ")
        )
        .map((item) =>
          createRow(item, "fertigation", {
            categoryKey: "hdpe-ez-fertigation",
            typeLabel: "HDPE",
            typeKey: "hdpe",
            hdpeSubtypeKey: "ez-fertigation",
            hdpeSubtypeLabel: "EZ Fertigation",
            dueFieldPriority: ["NPFertigationDue"],
          })
        )
        .filter(Boolean)
        .filter((row) => row.jobNumber !== "N/A");

      const bmxRows = safeGeneralProject
        .filter((item) => item.ModelNumber && item.ModelNumber.startsWith("BMX"))
        .map((item) =>
          createRow(item, "generalProject", {
            categoryKey: "hdpe-bmx",
            typeLabel: "HDPE",
            typeKey: "hdpe",
            hdpeSubtypeKey: "bmx",
            hdpeSubtypeLabel: "BMX",
            dueFieldPriority: ["NPBMXDue"],
          })
        )
        .filter(Boolean)
        .filter((row) => row.jobNumber !== "N/A");

      const horizontalInducerRows = safePumpManifoldPiping
        .filter(
          (item) =>
            item.HasPiping === true &&
            item.HDPEAccessoryForSubPump === "Horizontal Inducer Tube"
        )
        .map((item) =>
          createRow(item, "pumpManifoldPiping", {
            categoryKey: "hdpe-horizontal-inducer",
            typeLabel: "HDPE",
            typeKey: "hdpe",
            hdpeSubtypeKey: "horizontal-inducer",
            hdpeSubtypeLabel: "Horizontal Inducer Tube",
            dueFieldPriority: ["NPInducerTubeDue"],
          })
        )
        .filter(Boolean)
        .filter((row) => row.jobNumber !== "N/A");

      const rocketRows = safePumpManifoldPiping
        .filter(
          (item) =>
            item.HasPiping === true &&
            item.HDPEAccessoryForSubPump === "Vertical Inducer Tube (Rocket)"
        )
        .map((item) =>
          createRow(item, "pumpManifoldPiping", {
            categoryKey: "hdpe-rocket",
            typeLabel: "HDPE",
            typeKey: "hdpe",
            hdpeSubtypeKey: "rocket",
            hdpeSubtypeLabel: "Vertical Inducer Tube (Rocket)",
            dueFieldPriority: ["NPInducerTubeDue"],
          })
        )
        .filter(Boolean)
        .filter((row) => row.jobNumber !== "N/A");

      const hdpeSuctionRows = safeHcRequirements
        .filter(
          (item) =>
            item.SuctionLineMaterial === "HDPE" &&
            item.SuctionLinesBy === "Watertronics"
        )
        .map((item) =>
          createRow(item, "hcRequirements", {
            categoryKey: "hdpe-suction-line",
            typeLabel: "HDPE",
            typeKey: "hdpe",
            hdpeSubtypeKey: "suction-line",
            hdpeSubtypeLabel: "Suction Line",
            dueFieldPriority: ["NPSuctionLineDue"],
          })
        )
        .filter(Boolean)
        .filter((row) => row.jobNumber !== "N/A");

      const allRows = [
        ...enclosureRows,
        ...ezFertigationRows,
        ...bmxRows,
        ...horizontalInducerRows,
        ...rocketRows,
        ...hdpeSuctionRows,
      ].sort((a, b) => {
        // Inverted sort order: earliest due dates first.
        if (a.dueSortValue !== b.dueSortValue) {
          return a.dueSortValue - b.dueSortValue;
        }

        return a.jobNumber.localeCompare(b.jobNumber, undefined, { numeric: true });
      });

      if (snapshotOnly) return allRows;
      setScheduleRows(allRows);
    } catch (fetchError) {
      if (snapshotOnly) throw fetchError;
      console.error("Failed to fetch NP weekly schedule data:", fetchError);
      setError("Failed to load North Prairie schedule data.");
      setScheduleRows([]);
    } finally {
      if (!snapshotOnly) setLoading(false);
    }
  }, [
    apiUrls,
    dispatch,
  ]);

  useGuardedRealtimeList({ apiKey: 'generalProject', scopeKey: apiUrls, blocked: loading,
    additionalResources: ['portal:station-enclosure', 'portal:fertigation', 'portal:pump-manifold-piping', 'portal:hc-requirements'],
    load: ({ signal }) => fetchNpScheduleRows({ snapshotOnly: true, signal }), apply: setScheduleRows });

  useEffect(() => {
    fetchNpScheduleRows();
  }, [fetchNpScheduleRows]);

  useEffect(() => {
    if (initializedWeeksRef.current) {
      return;
    }

    // Ensure Week 1 always starts at the current week for this view.
    refreshWeekData(null, 0, NP_WEEK_COUNT);
    initializedWeeksRef.current = true;
  }, [refreshWeekData]);

  const typeCounts = useMemo(() => {
    const counts = {
      enclosure: 0,
      hdpe: 0,
    };

    scheduleRows.forEach((row) => {
      if (row.typeKey === "enclosure") {
        counts.enclosure += 1;
      }

      if (row.typeKey === "hdpe") {
        counts.hdpe += 1;
      }
    });

    return counts;
  }, [scheduleRows]);

  const hdpeSubtypeCounts = useMemo(() => {
    const counts = {
      "ez-fertigation": 0,
      bmx: 0,
      "horizontal-inducer": 0,
      rocket: 0,
      "suction-line": 0,
    };

    scheduleRows.forEach((row) => {
      if (row.typeKey !== "hdpe" || !row.hdpeSubtypeKey) {
        return;
      }

      if (counts[row.hdpeSubtypeKey] !== undefined) {
        counts[row.hdpeSubtypeKey] += 1;
      }
    });

    return counts;
  }, [scheduleRows]);

  const availableTypeOptions = useMemo(() => {
    return TYPE_FILTER_OPTIONS.filter((option) => {
      if (option.value === "all") {
        return true;
      }

      return typeCounts[option.value] > 0;
    });
  }, [typeCounts]);

  const availableHdpeSubtypeOptions = useMemo(() => {
    return HDPE_SUBTYPE_OPTIONS.filter((option) => {
      if (option.value === "all") {
        return true;
      }

      return hdpeSubtypeCounts[option.value] > 0;
    });
  }, [hdpeSubtypeCounts]);

  useEffect(() => {
    const selectedTypeStillAvailable = availableTypeOptions.some((option) => option.value === typeFilter);

    if (!selectedTypeStillAvailable) {
      setTypeFilter("all");
    }
  }, [availableTypeOptions, typeFilter]);

  useEffect(() => {
    const selectedSubtypeStillAvailable = availableHdpeSubtypeOptions.some(
      (option) => option.value === hdpeSubtypeFilter
    );

    if (!selectedSubtypeStillAvailable) {
      setHdpeSubtypeFilter("all");
    }
  }, [availableHdpeSubtypeOptions, hdpeSubtypeFilter]);

  const filteredRows = useMemo(() => {
    return scheduleRows.filter((row) => {
      const matchesType = typeFilter === "all" || row.typeKey === typeFilter;

      if (hdpeSubtypeFilter !== "all") {
        return row.typeKey === "hdpe" && row.hdpeSubtypeKey === hdpeSubtypeFilter;
      }

      return matchesType;
    });
  }, [scheduleRows, typeFilter, hdpeSubtypeFilter]);

  const weeklyGroups = useMemo(() => {
    const groupedMap = new Map();
    const firstWeekStart = weekColumns[0]?.startDate ? moment(weekColumns[0].startDate) : null;
    const lastWeekEnd = weekColumns[weekColumns.length - 1]?.endDate
      ? moment(weekColumns[weekColumns.length - 1].endDate)
      : null;

    const ensureGroup = (key, label, sortValue) => {
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          key,
          label,
          sortValue,
          rows: [],
        });
      }

      return groupedMap.get(key);
    };

    filteredRows.forEach((row) => {
      if (!row.normalizedDueDate) {
        ensureGroup("no-due-date", "No Due Date", Number.MAX_SAFE_INTEGER - 1).rows.push(row);
        return;
      }

      const weekIndex = getWeekForDate(row.normalizedDueDate, false);

      if (weekIndex !== null && weekColumns[weekIndex]) {
        const weekMeta = weekColumns[weekIndex];
        const label = weekMeta.label || `Week ${weekIndex + 1}`;

        ensureGroup(`week-${weekIndex}`, label, weekIndex).rows.push(row);
        return;
      }

      const dueMoment = moment(row.normalizedDueDate, "YYYY-MM-DD", true);

      if (!dueMoment.isValid()) {
        ensureGroup("invalid-due-date", "Invalid Due Date", Number.MAX_SAFE_INTEGER).rows.push(row);
        return;
      }

      if (firstWeekStart && dueMoment.isBefore(firstWeekStart, "day")) {
        ensureGroup(
          "before-week-1",
          "Before Week 1",
          Number.MAX_SAFE_INTEGER - 3
        ).rows.push(row);
        return;
      }

      if (lastWeekEnd && dueMoment.isAfter(lastWeekEnd, "day")) {
        ensureGroup(
          "after-visible-weeks",
          `After Week ${weekColumns.length}`,
          Number.MAX_SAFE_INTEGER - 2
        ).rows.push(row);
        return;
      }

      ensureGroup("outside-visible-weeks", "Outside Visible Weeks", Number.MAX_SAFE_INTEGER - 2).rows.push(row);
    });

    return Array.from(groupedMap.values()).sort((a, b) => a.sortValue - b.sortValue);
  }, [filteredRows, getWeekForDate, weekColumns]);

  const handleTypeFilterChange = (event) => {
    const nextTypeFilter = event.target.value;
    setTypeFilter(nextTypeFilter);

    if (nextTypeFilter !== "hdpe") {
      setHdpeSubtypeFilter("all");
    }
  };

  const handleHdpeSubtypeChange = (event) => {
    const nextSubtype = event.target.value;
    setHdpeSubtypeFilter(nextSubtype);

    if (nextSubtype !== "all") {
      setTypeFilter("hdpe");
    }
  };

  if (loading) {
    return (
      <div className="m-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-4">
        {error}
      </Alert>
    );
  }

  if (weekColumns.length === 0) {
    return (
      <div className="m-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="m-3">
      <Row className="mb-3 align-items-end">
        <Col md={4} lg={3}>
          <Form.Group>
            <Form.Label><strong>Type</strong></Form.Label>
            <Form.Select value={typeFilter} onChange={handleTypeFilterChange}>
              {availableTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={5} lg={4}>
          <Form.Group>
            <Form.Label><strong>HDPE Type</strong></Form.Label>
            <Form.Select
              value={hdpeSubtypeFilter}
              onChange={handleHdpeSubtypeChange}
              disabled={typeFilter === "enclosure"}
            >
              {availableHdpeSubtypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3} lg={2}>
          <Alert variant="light" className="mb-0 text-center py-2">
            <strong>{filteredRows.length}</strong> items
          </Alert>
        </Col>
      </Row>

      {scheduleRows.length === 0 ? (
        <Alert variant="info">No North Prairie schedule items found.</Alert>
      ) : weeklyGroups.length === 0 ? (
        <Alert variant="info">No North Prairie schedule items match the selected filters.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>Type</th>
              <th>Job Number</th>
              <th>Project Name</th>
              <th>Model Number</th>
              <th>Job Status</th>
              <th>Due Date</th>
            </tr>
          </thead>
          <tbody>
            {weeklyGroups.map((group) => (
              <React.Fragment key={group.key}>
                <tr className="table-primary">
                  <td colSpan={6}>
                    <strong>{group.label}</strong>
                    <span className="ms-2 text-muted">({group.rows.length})</span>
                  </td>
                </tr>

                {group.rows.map((row) => {
                  const formattedDueDate = row.normalizedDueDate
                    ? formatDateUnified(row.normalizedDueDate, "display")
                    : "N/A";
                  const dueColorClass = getWeeksToShipColor(row.weeksToDue);
                  const hasValidProjectId = canOpenPortalJob(row) && row.projectId !== null && row.projectId !== undefined && row.projectId !== "";

                  return (
                    <tr key={row.rowId}>
                      <td>
                        <div>{row.type}</div>
                        {row.typeKey === "hdpe" && row.hdpeSubtypeLabel && (
                          <small className="text-muted">{row.hdpeSubtypeLabel}</small>
                        )}
                      </td>
                      <td
                        onClick={() => {
                          if (hasValidProjectId) {
                            navigate(`/${row.projectId}/generalInfo`);
                          }
                        }}
                        style={{ cursor: hasValidProjectId ? "pointer" : "default" }}
                      >
                        {row.jobNumber}
                      </td>
                      <td>{row.projectName}</td>
                      <td>{row.modelNumber}</td>
                      <td>{row.jobStatus || "N/A"}</td>
                      <td className={dueColorClass} title={row.dueDateSource || ""}>{formattedDueDate}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default NPWeeklyScheduleTable;
